import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Seo from "@/components/Seo";

export default function LoginPage() {
    const { login, user } = useAuth();
    const { t } = useTranslation();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/dashboard";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (user && typeof user === "object") {
            const target = user.role === "company" ? "/company"
                : user.role === "admin" && next === "/dashboard" ? "/admin"
                : next;
            nav(target, { replace: true });
        }
    }, [user, nav, next]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        const res = await login(email, password);
        setBusy(false);
        if (res.ok) {
            toast.success(t("auth.welcomeBack"));
        } else {
            toast.error(res.error || t("auth.loginBusy"));
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <Seo title="Anmelden | Bewerbung24.ai" description="Melde dich bei Bewerbung24.ai an und erstelle deine nächste KI-Bewerbung." path="/login" noindex />
            <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden" style={{ background: "var(--gradient-primary)" }}>
                <Link to="/" className="flex items-center gap-2 text-white">
                    <span className="b24-logo-shine"><img src="/logo-icon.png" alt="bewerbung.ai" className="h-9 w-auto object-contain bg-white/10 rounded-lg p-1" /></span>
                    <span className="font-heading font-bold tracking-tight b24-shine-white">Bewerbung24.ai</span>
                </Link>
                <div className="relative">
                    <div className="absolute -top-32 -right-12 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
                    <h2 className="font-heading text-4xl xl:text-5xl text-white font-bold tracking-tight leading-tight">
                        {t("auth.leftPaneTitle")}
                    </h2>
                    <p className="mt-4 text-white/85 text-lg max-w-md">{t("auth.leftPaneSub")}</p>
                </div>
                <p className="text-white/70 text-sm">"{t("home.testimonials.t1Text")}" — {t("home.testimonials.t1Author")}</p>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md">
                    <div className="flex items-center justify-between mb-8">
                        <Link to="/" className="lg:hidden flex items-center gap-2">
                            <span className="b24-logo-shine"><img src="/logo-icon.png" alt="bewerbung.ai" className="h-9 w-auto object-contain" /></span>
                            <span className="font-heading font-bold tracking-tight"><span className="b24-shine-dark">Bewerbung24</span><span className="b24-shine-red">.ai</span></span>
                        </Link>
                        <div className="ml-auto"><LanguageSwitcher /></div>
                    </div>

                    <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">{t("auth.loginTitle")}</h1>
                    <p className="mt-2 text-slate-600">{t("auth.loginSubtitle")} <Link to="/registrieren" className="text-red-600 font-semibold hover:underline">{t("auth.registerLink")}</Link></p>

                    <form className="mt-8 space-y-4" onSubmit={submit} data-testid="login-form">
                        <div>
                            <label className="text-sm font-medium text-slate-700">{t("common.email")}</label>
                            <div className="mt-1 relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <input type="email" required autoComplete="email" data-testid="login-email-input"
                                    value={email} onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none"
                                    placeholder="email@example.com" />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-slate-700">{t("common.password")}</label>
                            <div className="mt-1 relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <input type={show ? "text" : "password"} required autoComplete="current-password" data-testid="login-password-input"
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-10 pr-10 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none"
                                    placeholder="••••••••" />
                                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                    {show ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <Link to="/passwort-vergessen" className="text-sm text-red-600 hover:underline">
                                {t("auth.forgot")}
                            </Link>
                        </div>
                        <button type="submit" disabled={busy} data-testid="login-submit-button"
                            className="w-full py-3 rounded-full font-semibold btn-primary-grad disabled:opacity-60">
                            {busy ? t("auth.loginBusy") : t("auth.submitLogin")}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
